import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Search, Download, LogOut, Inbox, Sparkles, Copy, X, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const STATUS_META = {
  new: { label: "New", cls: "bg-[#AEE6D5]/15 text-[#AEE6D5] border-[#AEE6D5]/30" },
  contacted: { label: "Contacted", cls: "bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/30" },
  booked: { label: "Booked", cls: "bg-emerald-400/15 text-emerald-300 border-emerald-400/30" },
};
const STATUS_ORDER = ["new", "contacted", "booked"];

const DATE_OPTIONS = [
  { v: "all", label: "All time" },
  { v: "7", label: "Last 7 days" },
  { v: "30", label: "Last 30 days" },
  { v: "90", label: "Last 90 days" },
];

const selectCls =
  "rounded-xl border border-[#AEE6D5]/25 bg-[#0B3B3C]/60 px-3 py-2.5 text-sm text-[#F8FAFC] outline-none transition-colors focus:border-[#D4AF37]";

const Badge = ({ label, value }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-3 py-1 text-xs capitalize text-[#D4AF37]">
    <span className="font-mono text-[0.55rem] uppercase tracking-[0.1em] text-[#F8FAFC]/50">{label}</span>
    {value}
  </span>
);

const scoreCls = (s) =>
  s >= 8
    ? "bg-emerald-400/15 text-emerald-300 border-emerald-400/40"
    : s >= 5
    ? "bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/40"
    : "bg-[#F8FAFC]/10 text-[#F8FAFC]/55 border-[#F8FAFC]/20";

const ScoreChip = ({ qualification }) => {
  if (!qualification || typeof qualification.fit_score !== "number") {
    return (
      <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#F8FAFC]/15 text-[0.6rem] text-[#F8FAFC]/40" title="Scoring…" data-testid="score-chip-pending">
        …
      </span>
    );
  }
  return (
    <span
      className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border text-sm font-bold ${scoreCls(qualification.fit_score)}`}
      title={`AI fit ${qualification.fit_score}/10 · urgency ${qualification.urgency || "?"}`}
      data-testid="score-chip"
    >
      {qualification.fit_score}
    </span>
  );
};

const StatusSelect = ({ value, onChange, testid }) => {
  const meta = STATUS_META[value] || STATUS_META.new;
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className={`rounded-full border px-3 py-1 text-[0.7rem] font-bold uppercase tracking-[0.08em] outline-none ${meta.cls}`}
    >
      {STATUS_ORDER.map((s) => (
        <option key={s} value={s} className="bg-[#0B3B3C] text-[#F8FAFC]">
          {STATUS_META[s].label}
        </option>
      ))}
    </select>
  );
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("all");
  const [budgetF, setBudgetF] = useState("all");
  const [dateF, setDateF] = useState("all");
  const [sortBy, setSortBy] = useState("priority");
  const [aiLead, setAiLead] = useState(null);
  const [qual, setQual] = useState(null);
  const [draft, setDraft] = useState(null);
  const [qualBusy, setQualBusy] = useState(false);
  const [draftBusy, setDraftBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const openAi = (row) => {
    setAiLead(row);
    setQual(row.qualification || null);
    setDraft(null);
    setCopied(false);
  };

  const runQualify = async () => {
    if (!aiLead) return;
    setQualBusy(true);
    try {
      const res = await fetch(`${API}/bookings/${aiLead.id}/qualify`, { method: "POST", credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setQual(data);
        setRows((prev) => prev.map((r) => (r.id === aiLead.id ? { ...r, qualification: data } : r)));
      }
    } finally {
      setQualBusy(false);
    }
  };

  const runDraft = async () => {
    if (!aiLead) return;
    setDraftBusy(true);
    setCopied(false);
    try {
      const res = await fetch(`${API}/bookings/${aiLead.id}/draft-reply`, { method: "POST", credentials: "include" });
      if (res.ok) setDraft(await res.json());
    } finally {
      setDraftBusy(false);
    }
  };

  const copyDraft = async () => {
    if (!draft) return;
    const text = `Subject: ${draft.subject}\n\n${draft.body}`;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      } catch {
        /* ignore — clipboard unavailable */
      }
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [meRes, bRes] = await Promise.all([
          fetch(`${API}/auth/me`, { credentials: "include" }),
          fetch(`${API}/bookings`, { credentials: "include" }),
        ]);
        if (meRes.ok) setUser(await meRes.json());
        if (bRes.ok) setRows(await bRes.json());
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const budgets = useMemo(() => [...new Set(rows.map((r) => r.budget).filter(Boolean))], [rows]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const now = Date.now();
    return rows.filter((r) => {
      const st = r.status || "new";
      if (statusF !== "all" && st !== statusF) return false;
      if (budgetF !== "all" && r.budget !== budgetF) return false;
      if (dateF !== "all") {
        const d = new Date(r.created_at).getTime();
        if (isNaN(d) || now - d > Number(dateF) * 86400000) return false;
      }
      if (s && ![r.name_org, r.event_goal, r.budget, r.contact].join(" ").toLowerCase().includes(s)) return false;
      return true;
    });
  }, [rows, q, statusF, budgetF, dateF]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    if (sortBy === "priority") {
      arr.sort(
        (a, b) =>
          (b.qualification?.fit_score ?? -1) - (a.qualification?.fit_score ?? -1) ||
          new Date(b.created_at) - new Date(a.created_at)
      );
    } else if (sortBy === "budget") {
      const rank = (r) => ({ high: 3, medium: 2, low: 1 }[r.qualification?.budget_tier] || 0);
      arr.sort((a, b) => rank(b) - rank(a) || new Date(b.created_at) - new Date(a.created_at));
    } else {
      arr.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }
    return arr;
  }, [filtered, sortBy]);

  const updateStatus = async (id, status) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
    try {
      await fetch(`${API}/bookings/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status }),
      });
    } catch {
      /* optimistic — keep local change */
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    } finally {
      navigate("/login", { replace: true });
    }
  };

  const exportCsv = () => {
    const headers = ["Submitted", "Status", "Name & Organization", "Event / Goal", "Location", "Venue", "Attendance", "Budget", "Contact", "Source", "AI Fit"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [headers.join(",")].concat(
      filtered.map((r) =>
        [fmtDate(r.created_at), (STATUS_META[r.status || "new"] || {}).label, r.name_org, r.event_goal, r.event_location, r.venue, r.headcount, r.budget, r.contact, r.source || "form", r.qualification?.fit_score ?? ""]
          .map(esc)
          .join(",")
      )
    );
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `caseys-cadence-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#0A2E2C] text-[#F8FAFC]" data-testid="dashboard">
      <header className="sticky top-0 z-20 border-b border-[#AEE6D5]/12 bg-[#0A2E2C]/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-8">
          <div>
            <p className="font-mono text-[0.6rem] uppercase tracking-[0.24em] text-[#D4AF37]">Casey&#39;s Cadence</p>
            <h1 className="font-serif text-2xl font-light">Leads Dashboard</h1>
          </div>
          <div className="flex items-center gap-3">
            {user && <span className="hidden text-xs text-[#F8FAFC]/60 sm:inline" data-testid="dashboard-user">{user.email}</span>}
            <Link
              to="/scout"
              data-testid="dashboard-scout-link"
              className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-4 py-2 text-xs font-bold uppercase tracking-[0.1em] text-[#0A2E2C] transition-colors hover:bg-[#B99628]"
            >
              <Sparkles className="h-3.5 w-3.5" /> Booking Scout
            </Link>
            <button
              onClick={logout}
              data-testid="dashboard-logout"
              className="inline-flex items-center gap-2 rounded-full border border-[#AEE6D5]/30 px-4 py-2 text-xs font-bold uppercase tracking-[0.1em] text-[#AEE6D5] transition-colors hover:bg-[#AEE6D5]/10"
            >
              <LogOut className="h-3.5 w-3.5" /> Log out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-8 sm:px-8">
        {/* count + search + export */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <span className="font-serif text-4xl font-light text-[#D4AF37]" data-testid="dashboard-count">{filtered.length}</span>
            <span className="font-mono text-[0.62rem] uppercase tracking-[0.2em] text-[#AEE6D5]">
              {filtered.length === rows.length ? "Total inquiries" : `of ${rows.length} inquiries`}
            </span>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#F8FAFC]/40" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search inquiries…"
                data-testid="dashboard-search"
                className="w-full rounded-xl border border-[#AEE6D5]/25 bg-[#0B3B3C]/60 py-2.5 pl-10 pr-4 text-sm text-[#F8FAFC] placeholder-[#F8FAFC]/40 outline-none transition-colors focus:border-[#D4AF37] sm:w-56"
              />
            </div>
            <button
              onClick={exportCsv}
              disabled={filtered.length === 0}
              data-testid="dashboard-export"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#D4AF37] px-5 py-2.5 text-sm font-bold text-[#0A2E2C] transition-colors hover:bg-[#B99628] disabled:opacity-50"
            >
              <Download className="h-4 w-4" /> Export CSV
            </button>
          </div>
        </div>

        {/* filters */}
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <span className="font-mono text-[0.58rem] uppercase tracking-[0.2em] text-[#F8FAFC]/45">Filter</span>
          <select value={statusF} onChange={(e) => setStatusF(e.target.value)} data-testid="filter-status" className={selectCls}>
            <option value="all" className="bg-[#0B3B3C]">All statuses</option>
            {STATUS_ORDER.map((s) => (
              <option key={s} value={s} className="bg-[#0B3B3C]">{STATUS_META[s].label}</option>
            ))}
          </select>
          <select value={budgetF} onChange={(e) => setBudgetF(e.target.value)} data-testid="filter-budget" className={selectCls}>
            <option value="all" className="bg-[#0B3B3C]">All budgets</option>
            {budgets.map((b) => (
              <option key={b} value={b} className="bg-[#0B3B3C]">{b}</option>
            ))}
          </select>
          <select value={dateF} onChange={(e) => setDateF(e.target.value)} data-testid="filter-date" className={selectCls}>
            {DATE_OPTIONS.map((d) => (
              <option key={d.v} value={d.v} className="bg-[#0B3B3C]">{d.label}</option>
            ))}
          </select>
          <span className="ml-auto font-mono text-[0.58rem] uppercase tracking-[0.2em] text-[#F8FAFC]/45">Sort</span>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="sort-by" className={selectCls}>
            <option value="priority" className="bg-[#0B3B3C]">Priority (AI fit)</option>
            <option value="budget" className="bg-[#0B3B3C]">Budget tier</option>
            <option value="newest" className="bg-[#0B3B3C]">Newest</option>
          </select>
          {(statusF !== "all" || budgetF !== "all" || dateF !== "all" || q) && (
            <button
              onClick={() => { setStatusF("all"); setBudgetF("all"); setDateF("all"); setQ(""); }}
              data-testid="filter-clear"
              className="text-xs font-mono uppercase tracking-[0.12em] text-[#AEE6D5]/70 underline-offset-4 transition-colors hover:text-[#AEE6D5] hover:underline"
            >
              Clear
            </button>
          )}
        </div>

        {/* content */}
        <div className="mt-8">
          {loading ? (
            <p className="py-20 text-center font-mono text-sm uppercase tracking-[0.2em] text-[#AEE6D5]/70">Loading inquiries…</p>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center gap-4 rounded-3xl border border-dashed border-[#AEE6D5]/20 py-20 text-center" data-testid="dashboard-empty">
              <Inbox className="h-10 w-10 text-[#AEE6D5]/50" strokeWidth={1.4} />
              <p className="text-sm text-[#F8FAFC]/60">
                {rows.length === 0 ? "No inquiries yet — they'll appear here the moment someone books." : "No inquiries match your filters."}
              </p>
            </div>
          ) : (
            <>
              {/* desktop table */}
              <div className="hidden overflow-hidden rounded-2xl border border-[#AEE6D5]/12 md:block">
                <table className="w-full text-left text-sm">
                  <thead className="bg-[#0B3B3C] font-mono text-[0.6rem] uppercase tracking-[0.16em] text-[#AEE6D5]">
                    <tr>
                      <th className="px-5 py-4 font-medium">Submitted</th>
                      <th className="px-5 py-4 font-medium">Status</th>
                      <th className="px-5 py-4 font-medium">Name &amp; Organization</th>
                      <th className="px-5 py-4 font-medium">Event / Goal</th>
                      <th className="px-5 py-4 font-medium">Budget</th>
                      <th className="px-5 py-4 font-medium">Contact</th>
                      <th className="px-5 py-4 font-medium text-right">AI</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((r, i) => (
                      <tr key={r.id || i} className="border-t border-[#AEE6D5]/10 align-top transition-colors hover:bg-[#0B3B3C]/50" data-testid="booking-row-desktop">
                        <td className="whitespace-nowrap px-5 py-4 text-[#F8FAFC]/60">{fmtDate(r.created_at)}</td>
                        <td className="px-5 py-4"><StatusSelect value={r.status || "new"} onChange={(s) => updateStatus(r.id, s)} testid="status-select-desktop" /></td>
                        <td className="px-5 py-4 font-medium text-[#F8FAFC]">
                          <div className="flex items-center gap-3">
                            <ScoreChip qualification={r.qualification} />
                            <span>
                              {r.name_org}
                              {r.source === "chatbot" && <span className="ml-2 rounded-full bg-[#AEE6D5]/15 px-2 py-0.5 text-[0.55rem] font-bold uppercase tracking-[0.08em] text-[#AEE6D5]">via chat</span>}
                            </span>
                          </div>
                        </td>
                        <td className="max-w-xs px-5 py-4 text-[#F8FAFC]/80">
                          {r.event_goal}
                          {(r.event_location || r.headcount || r.venue) && (
                            <span className="mt-1 block text-[0.7rem] text-[#AEE6D5]/70">
                              {[r.event_location, r.venue, r.headcount && `${r.headcount} ppl`].filter(Boolean).join(" · ")}
                            </span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-5 py-4 text-[#D4AF37]">{r.budget}</td>
                        <td className="px-5 py-4 text-[#F8FAFC]/80">{r.contact}</td>
                        <td className="px-5 py-4 text-right">
                          <button
                            onClick={() => openAi(r)}
                            data-testid="lead-ai-btn"
                            className="inline-flex items-center gap-1.5 rounded-full border border-[#D4AF37]/50 px-3 py-1.5 text-xs font-bold text-[#D4AF37] transition-colors hover:bg-[#D4AF37] hover:text-[#0A2E2C]"
                          >
                            <Sparkles className="h-3.5 w-3.5" /> AI
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* mobile cards */}
              <div className="space-y-4 md:hidden">
                {sorted.map((r, i) => (
                  <div key={r.id || i} className="rounded-2xl border border-[#AEE6D5]/12 bg-[#0B3B3C]/50 p-5" data-testid="booking-row-mobile">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2.5">
                        <ScoreChip qualification={r.qualification} />
                        <p className="font-medium text-[#F8FAFC]">
                          {r.name_org}
                          {r.source === "chatbot" && <span className="ml-2 rounded-full bg-[#AEE6D5]/15 px-2 py-0.5 text-[0.5rem] font-bold uppercase tracking-[0.08em] text-[#AEE6D5]">via chat</span>}
                        </p>
                      </div>
                      <span className="shrink-0 rounded-full bg-[#D4AF37]/15 px-2.5 py-1 text-[0.65rem] font-bold text-[#D4AF37]">{r.budget}</span>
                    </div>
                    <p className="mt-2 text-sm text-[#F8FAFC]/80">{r.event_goal}</p>
                    {(r.event_location || r.headcount || r.venue) && (
                      <p className="mt-1 text-xs text-[#AEE6D5]/70">{[r.event_location, r.venue, r.headcount && `${r.headcount} ppl`].filter(Boolean).join(" · ")}</p>
                    )}
                    <p className="mt-3 text-sm text-[#AEE6D5]">{r.contact}</p>
                    <div className="mt-4 flex items-center justify-between">
                      <p className="font-mono text-[0.6rem] uppercase tracking-[0.14em] text-[#F8FAFC]/45">{fmtDate(r.created_at)}</p>
                      <StatusSelect value={r.status || "new"} onChange={(s) => updateStatus(r.id, s)} testid="status-select-mobile" />
                    </div>
                    <button
                      onClick={() => openAi(r)}
                      data-testid="lead-ai-btn"
                      className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-[#D4AF37]/50 py-2.5 text-xs font-bold text-[#D4AF37] transition-colors hover:bg-[#D4AF37] hover:text-[#0A2E2C]"
                    >
                      <Sparkles className="h-4 w-4" /> AI assist
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </main>

      {/* AI lead assist modal */}
      {aiLead && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 p-4" data-testid="lead-ai-modal" onClick={() => setAiLead(null)}>
          <div className="max-h-[88vh] w-full max-w-lg overflow-y-auto rounded-3xl border border-[#AEE6D5]/20 bg-[#0A2E2C] p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-mono text-[0.58rem] uppercase tracking-[0.22em] text-[#D4AF37]">AI Lead Assist</p>
                <h3 className="mt-1 font-serif text-xl text-[#F8FAFC]">{aiLead.name_org}</h3>
                <p className="mt-1 text-sm text-[#F8FAFC]/70">{aiLead.event_goal}</p>
                <p className="mt-1 text-xs text-[#AEE6D5]">{[aiLead.event_location, aiLead.venue, aiLead.headcount && `${aiLead.headcount} ppl`, aiLead.budget].filter(Boolean).join(" · ")}</p>
                <p className="mt-0.5 text-xs text-[#F8FAFC]/50">{aiLead.contact}</p>
              </div>
              <button onClick={() => setAiLead(null)} data-testid="lead-ai-close" className="rounded-full p-1.5 text-[#F8FAFC]/60 transition-colors hover:bg-white/10 hover:text-[#F8FAFC]">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Qualification */}
            <div className="mt-6">
              <div className="flex items-center justify-between">
                <p className="font-mono text-[0.6rem] uppercase tracking-[0.18em] text-[#AEE6D5]">Lead qualification</p>
                <button onClick={runQualify} disabled={qualBusy} data-testid="lead-qualify-btn" className="inline-flex items-center gap-1.5 rounded-full bg-[#D4AF37] px-3 py-1.5 text-xs font-bold text-[#0A2E2C] transition-colors hover:bg-[#B99628] disabled:opacity-60">
                  {qualBusy ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Analyzing…</> : <><Sparkles className="h-3.5 w-3.5" /> {qual ? "Re-analyze" : "Qualify"}</>}
                </button>
              </div>
              {qual && (
                <div className="mt-3 space-y-3 rounded-2xl border border-[#AEE6D5]/12 bg-[#0B3B3C]/50 p-4" data-testid="lead-qual-result">
                  <p className="text-sm text-[#F8FAFC]/85">{qual.summary}</p>
                  <div className="flex flex-wrap gap-2">
                    <Badge label="Fit" value={`${qual.fit_score}/10`} />
                    <Badge label="Budget" value={qual.budget_tier} />
                    <Badge label="Urgency" value={qual.urgency} />
                  </div>
                  <p className="text-sm text-[#AEE6D5]"><span className="text-[#F8FAFC]/50">Next: </span>{qual.recommended_action}</p>
                </div>
              )}
            </div>

            {/* Draft reply */}
            <div className="mt-6">
              <div className="flex items-center justify-between">
                <p className="font-mono text-[0.6rem] uppercase tracking-[0.18em] text-[#AEE6D5]">Draft reply</p>
                <button onClick={runDraft} disabled={draftBusy} data-testid="lead-draft-btn" className="inline-flex items-center gap-1.5 rounded-full bg-[#D4AF37] px-3 py-1.5 text-xs font-bold text-[#0A2E2C] transition-colors hover:bg-[#B99628] disabled:opacity-60">
                  {draftBusy ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Writing…</> : <><Sparkles className="h-3.5 w-3.5" /> {draft ? "Regenerate" : "Draft email"}</>}
                </button>
              </div>
              {draft && (
                <div className="mt-3 rounded-2xl border border-[#AEE6D5]/12 bg-[#0B3B3C]/50 p-4" data-testid="lead-draft-result">
                  <p className="text-xs font-bold text-[#D4AF37]">{draft.subject}</p>
                  <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-[#F8FAFC]/85">{draft.body}</p>
                  <button onClick={copyDraft} data-testid="lead-draft-copy" className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#AEE6D5]/30 px-4 py-2 text-xs font-bold text-[#AEE6D5] transition-colors hover:bg-[#AEE6D5]/10">
                    <Copy className="h-3.5 w-3.5" /> {copied ? "Copied!" : "Copy email"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
